import { Inject, Injectable } from '@angular/core';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { KEY_NOTIFICATION_COMPLETED } from '../concurrent-usage/concurrent-usage-keys';
import { from, Observable, of } from 'rxjs';
import {
    JsonNotificationStatusListModel,
    NotificationStatusListModel,
} from '../../interfaces/notification-status.interface';
import { NotificationStatusModelFromJson } from '../../models/low-level/notification-status.model';
import { catchError, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../../models/low-level/notification-identifier.model';

export const KEY_NOTIFICATION_STATUS = 'statusOfNotifications';
// Defined with the other device-handover keys; re-exported here for this file's existing importers.
export { KEY_NOTIFICATION_COMPLETED };
const KEY_CANCELLATION_CONFIRMED = 'cancellationConfirmed';
const KEY_CONFIRMATION_STARTED = 'confirmationStarted';

@Injectable({ providedIn: 'root' })
export class StorageNotificationStatusService {
    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    public getAllNotificationsStatusModelFromStorage(): Observable<NotificationStatusListModel> {
        return this.concurrentUsageService.readDataForKey(KEY_NOTIFICATION_STATUS).pipe(
            map((result) => {
                if (result?.length > 0) {
                    const notificationStatusList: JsonNotificationStatusListModel = JSON.parse(result[0].value);
                    const jsonNotificationStatusModelList = notificationStatusList.notificationStatusList;
                    return {
                        notificationStatusList: jsonNotificationStatusModelList.map(
                            (jsonNotificationStatusModel) =>
                                new NotificationStatusModelFromJson(jsonNotificationStatusModel),
                        ),
                        lastSavedTimestamp: notificationStatusList.lastSavedTimestamp,
                    };
                }
                throw new Error(
                    '[Concurrency Store Notification Status] Read notification status: no notification status found in store.',
                );
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Notification Status] Read notification status: ${error}`);
                return of({ notificationStatusList: [], lastSavedTimestamp: 0 });
            }),
        );
    }

    public saveAllNotificationStatusModelToStorage(
        notificationStatusArray: NotificationStatusListModel,
    ): Observable<void> {
        const outData = JSON.stringify(notificationStatusArray);
        this.log.debug(
            'StorageNotificationStatusService: saveAllNotificationStatusModelToStorage: Storing concurrent usage data for notification status list: \n',
            notificationStatusArray.notificationStatusList
                .map(
                    (notificationStatusModel) =>
                        `${notificationStatusModel.notificationId.toString()}: ${notificationStatusModel.status.join(' ')}`,
                )
                .join('\n'),
        );
        return this.concurrentUsageService
            .writeDataForKey(KEY_NOTIFICATION_STATUS, outData, notificationStatusArray.lastSavedTimestamp)
            .pipe(
                map(() => undefined),
                catchError((error) => {
                    this.log.warn(`[Concurrency Store Notification Status] Write notification status: ${error}`);
                    return of(undefined);
                }),
            );
    }

    public loadCompletedNotificationsToStorage(): Observable<NotificationCompletedListModel> {
        return this.concurrentUsageService.readDataForKey(KEY_NOTIFICATION_COMPLETED).pipe(
            map((result) => {
                if (result?.length > 0) {
                    return JSON.parse(result[0].value);
                }
                throw new Error(
                    '[Concurrency Store Notification Completed] Read completed notifications: no completed notifications found in store.',
                );
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Notification Completed] Read completed notification: ${error}`);
                return of(new NotificationCompletedListModel());
            }),
        );
    }

    public saveCompletedNotificationsToStorage(
        completedNotifications: NotificationCompletedListModel,
    ): Observable<void> {
        const outData = JSON.stringify(completedNotifications);
        this.log.debug(
            'StorageNotificaitonStatusConcurrentService: saveCompletedNotificationsToStorage: Storing concurrent usage data for completed notification list: \n',
            Object.keys(completedNotifications.completedNotifications).join(', '),
        );
        return this.concurrentUsageService
            .writeDataForKey(KEY_NOTIFICATION_COMPLETED, outData, completedNotifications.lastSavedTimestamp)
            .pipe(
                map(() => undefined),
                catchError((error) => {
                    this.log.warn(`[Concurrency Store Notification Completed] Write completed notifications: ${error}`);
                    return of(undefined);
                }),
            );
    }

    // Methods to save/remove/get cancellation confirmation information for an inprogress notification
    public saveCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): Observable<void> {
        return from(this.localStorageCommonService.storageSetAsync(KEY_CANCELLATION_CONFIRMED, notificationId));
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.localStorageCommonService.storageRemove(KEY_CANCELLATION_CONFIRMED);
    }

    public async getCancellationConfirmationForInprogress(
        notificationId: NotificationIdentifierModel,
    ): Promise<boolean> {
        return this.localStorageCommonService
            .storageGetAsync<JsonNotificationIdentifierModel>(KEY_CANCELLATION_CONFIRMED)
            .then((data) => {
                return !!data && new NotificationIdentifierModelFromJson(data).equalsTo(notificationId);
            });
    }

    public saveConfirmationStarted(notificationId: NotificationIdentifierModel): Observable<void> {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        return from(this.localStorageCommonService.storageSetAsync(id, true));
    }

    public async getConfirmationStartedAsync(notificationId: NotificationIdentifierModel): Promise<boolean> {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        return this.localStorageCommonService.storageGetAsync(id).then((started) => !!started);
    }

    public removeConfirmationStarted(notificationId: NotificationIdentifierModel): void {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        this.localStorageCommonService.storageRemove(id);
    }
}
